import { Helmet } from "react-helmet-async";
import { usePageSEO } from "@/hooks/usePageSEO";
import { useLocation } from "react-router-dom";

interface SEOMetaTagsProps {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  ogImage?: string;
  ogType?: string;
  noindex?: boolean;
  nofollow?: boolean;
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
  alternateLanguages?: {
    [key: string]: string;
  };
}

/**
 * SEO Meta Tags component con supporto DB-driven via usePageSEO.
 * I dati dal DB hanno priorità sui valori hardcoded passati come props.
 */
const SEOMetaTags = ({
  title: propTitle,
  description: propDescription,
  keywords: propKeywords,
  canonical: propCanonical,
  ogImage: propOgImage = "https://fabbrichetoscane.it/og-image.png",
  ogType = "website",
  noindex: propNoindex = false,
  nofollow: propNofollow = false,
  author = "Fabbriche Toscane",
  publishedTime,
  modifiedTime,
  alternateLanguages
}: SEOMetaTagsProps) => {
  const location = useLocation();
  const { data: seoData } = usePageSEO(location.pathname);

  // DB values override props (fallback to props)
  const title = seoData?.meta_title || propTitle;
  const description = seoData?.meta_description || propDescription;
  const keywords = seoData?.meta_keywords || propKeywords;
  const ogImage = seoData?.og_image || propOgImage;
  const noindex = seoData?.noindex ?? propNoindex;
  const nofollow = seoData?.nofollow ?? propNofollow;
  const canonical = seoData?.canonical_url || propCanonical;

  const siteUrl = typeof window !== 'undefined' ? window.location.origin : 'https://fabbrichetoscane.it';
  const currentUrl = canonical || (typeof window !== 'undefined' ? window.location.href : '');
  const fullOgImage = ogImage.startsWith("http") ? ogImage : `${siteUrl}${ogImage}`;
  
  const robotsContent = [
    noindex ? "noindex" : "index",
    nofollow ? "nofollow" : "follow",
    "max-snippet:-1",
    "max-image-preview:large",
    "max-video-preview:-1"
  ].join(", ");

  // Avoid duplicating "Fabbriche Toscane" suffix if title already contains it
  const fullTitle = /fabbriche\s+toscane/i.test(title) 
    ? title 
    : `${title} | Fabbriche Toscane`;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <meta name="author" content={author} />
      <meta name="robots" content={robotsContent} />
      <meta name="googlebot" content={robotsContent} />
      
      <link rel="canonical" href={currentUrl} />
      
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={seoData?.og_title || title} />
      <meta property="og:description" content={seoData?.og_description || description} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:image" content={fullOgImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content="Fabbriche Toscane" />
      <meta property="og:locale" content="it_IT" />
      <meta property="og:locale:alternate" content="en_US" />
      
      {ogType === "article" && publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {ogType === "article" && modifiedTime && (
        <meta property="article:modified_time" content={modifiedTime} />
      )}
      {ogType === "article" && author && (
        <meta property="article:author" content={author} />
      )}
      
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={seoData?.og_title || title} />
      <meta name="twitter:description" content={seoData?.og_description || description} />
      <meta name="twitter:image" content={fullOgImage} />
      
      <meta name="format-detection" content="telephone=no" />
      <meta httpEquiv="x-ua-compatible" content="IE=edge" />
      
      <meta name="geo.region" content="IT-52" />
      <meta name="geo.placename" content="Toscana" />
      <meta name="geo.position" content="43.7711;11.2486" />
      <meta name="ICBM" content="43.7711, 11.2486" />

      {alternateLanguages && Object.entries(alternateLanguages).map(([lang, url]) => (
        <link key={lang} rel="alternate" hrefLang={lang} href={url} />
      ))}
      {canonical && (
        <link rel="alternate" hrefLang="x-default" href={canonical} />
      )}
    </Helmet>
  );
};

export default SEOMetaTags;
